'use client';
import { useState } from 'react';
import Accordion from '@/components/accordion';
import IconButton from '@/components/action-button';
import MapComponent from '@/components/map';
import ServicesList from '@/components/services-list';
import { useViewportType } from '@/hooks';
import { Grid, GridItem, useTheme } from '@chakra-ui/react';
import { Divider } from '@ldfeplatform/componentlibrary.ui.atoms.divider';
import { Text } from '@ldfeplatform/componentlibrary.ui.atoms.text';
import { APIProvider } from '@vis.gl/react-google-maps';
import { FC } from 'react';
import { FaRegCompass } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import BeautyBoutiqueServices from '../beauty-boutique-services/beauty-boutique-services.component';
import { TLayout, TServiceProps, TStoreDetailsResults } from '../store-details/store-details.interface';
import { IStoreHours, IStoreInformation } from './store-information.interface';

import styles from './store-information.module.scss';

const storeInformationData = (layout: TLayout) => {
  const storeDetails = layout?.sections?.mainContentCollection?.components?.find(
    ({ componentId }) => componentId === 'storeDetailsComponent'
  )?.data as TStoreDetailsResults;

  const generalServices = layout?.sections?.mainContentCollection?.components?.find(
    ({ componentId }) => componentId === 'generalServicesComponent'
  )?.data as TServiceProps[];

  const beautyBoutiqueServices = layout?.sections?.mainContentCollection.components?.find(
    ({ componentId }) => componentId === 'bbServicesComponent'
  )?.data as TServiceProps[];

  return {
    storeDetails,
    generalServices,
    beautyBoutiqueServices,
  };
};

const StoreInformation: FC<IStoreInformation> = ({ layout, activeIndex }) => {
  const { formatMessage } = useIntl();
  const viewportType = useViewportType();
  const isMobileView = viewportType === 'mobile';
  const {
    common: { common02Regular, common03Regular },
    text: { deemphasized },
    sp02,
  } = useTheme();

  const { storeDetails, generalServices, beautyBoutiqueServices } = storeInformationData(layout);
  const hours = activeIndex === 2 ? storeDetails?.clinicHours : storeDetails?.storeHours;
  const getCanadaPostOfficeCTA = () => {
    const canadaPostOfficeCTA = storeDetails?.canadaPostOfficeCTA;
    return (
      <Text sx={{ ...common02Regular }} as="h3" mt="0.67rem">
        {formatMessage(
          { id: 'postOfficeHoursVary' },
          {
            a: (chunks) =>
              canadaPostOfficeCTA ? (
                <a href={canadaPostOfficeCTA} style={{ textDecoration: 'underline' }} target="_blank" rel="noreferrer">
                  {chunks}
                </a>
              ) : null,
          }
        )}
        .
      </Text>
    );
  };

  const getStoreHoursText = () => (
    <div className={styles.storeHours}>
      {hours?.map(({ timeRange, nameOfDay, isHoliday = false, holidayName = '' }: IStoreHours, index: number) => (
        <Grid key={index} className={styles.storeHoursGrid} sx={{ ...common02Regular }} mb={sp02}>
          <GridItem sx={{ textAlign: 'left' }}>
            <Text as="h3">{nameOfDay}:</Text>
            {isHoliday ? <Text sx={{ ...common03Regular, color: deemphasized }}>{`(${holidayName})`}</Text> : null}
          </GridItem>
          <GridItem colStart={2} sx={{ textAlign: 'right' }}>
            <Text as="h3">{timeRange}</Text>
            {isHoliday ? (
              <Text sx={{ ...common03Regular, color: deemphasized }}>{formatMessage({ id: 'holidayHours' })}</Text>
            ) : null}
          </GridItem>
        </Grid>
      ))}
      {activeIndex === 0 && getCanadaPostOfficeCTA()}
    </div>
  );

  const getContactInformationText = () => (
    <div>
      <div className={styles.contactInformation}>
        <Text sx={{ ...common02Regular }} as="h3" mr="1rem">
          {formatMessage({ id: 'phone' })}:
        </Text>
        <a href={`tel:${storeDetails?.phoneNumber}`}>
          <Text sx={{ ...common02Regular }} as="h3" textDecoration="underline">
            {storeDetails?.phoneNumber}
          </Text>
        </a>
      </div>
      {activeIndex === 1 && (
        <div>
          <div className={styles.contactInformation}>
            <Text sx={{ ...common02Regular }} as="h3" mr="1rem">
              {formatMessage({ id: 'email' })}:
            </Text>
            <a href={`mailto:${storeDetails?.email}`}>
              <Text sx={{ ...common02Regular }} as="h3" textDecoration="underline">
                {storeDetails?.email}
              </Text>
            </a>
          </div>
          <Text sx={{ ...common03Regular, color: deemphasized }} as="h4">
            {formatMessage({ id: 'emailDisclaimer' })}
          </Text>
          {storeDetails?.fax && (
            <div className={styles.contactInformation}>
              <Text sx={{ ...common02Regular }} as="h3" mr="1rem">
                {formatMessage({ id: 'fax' })}:
              </Text>
              <Text sx={{ ...common02Regular }} as="h3">
                {storeDetails?.fax}
              </Text>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const getAccordionGrid = () => (
    <div className={styles.rightContainer}>
      <Accordion heading={formatMessage({ id: 'storeHours' })} body={getStoreHoursText()} defaultOpenIndex={[0]} />
      <Divider />
      <Accordion
        heading={formatMessage({ id: 'contactInformation' })}
        body={getContactInformationText()}
        defaultOpenIndex={[0]}
      />
      <Divider />
    </div>
  );

  const getMapGrid = () => (
    <div className={styles.mapContainer}>
      <MapComponent
        initialPosition={{
          position: 1,
          latitude: Number(storeDetails?.map?.latitude),
          longitude: Number(storeDetails?.map?.longitude),
        }}
        positions={[
          {
            position: 1,
            latitude: Number(storeDetails?.map?.latitude),
            longitude: Number(storeDetails?.map?.longitude),
          },
        ]}
        onPinSelected={() => null}
        hasResults={false}
        handleMapInteraction={() => null}
        boundsUpdate={true}
        setBoundsUpdate={() => null}
      />
      {storeDetails?.map?.directionsURL ? (
        <div className={styles.directionsButton}>
          <IconButton
            icon={FaRegCompass}
            linkUrl={storeDetails?.map?.directionsURL}
            linkText={formatMessage({ id: 'getDirections' })}
            textDecoration="underline"
          />
        </div>
      ) : null}
    </div>
  );

  const getGeneralServices = (serviceType: string) => {
    if (!generalServices?.length) return null;

    return (
      <div className={styles.generalServices}>
        <ServicesList data={generalServices} titleId="generalServices" serviceType={serviceType} />
      </div>
    );
  };

  return (
    <>
      <APIProvider apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string}>
        <Grid {...(!isMobileView && { templateColumns: 'repeat(5, 1fr)' })}>
          {!isMobileView ? (
            <>
              <GridItem colSpan={2} overflow="auto" mr="2rem">
                {getAccordionGrid()}
              </GridItem>
              <GridItem colSpan={3}>{getMapGrid()}</GridItem>
            </>
          ) : (
            <>
              {getAccordionGrid()}
              {getMapGrid()}
            </>
          )}
        </Grid>
      </APIProvider>
      {activeIndex === 0 ? (
        <>
          {getGeneralServices('generalServices')}
          <BeautyBoutiqueServices bbServices={beautyBoutiqueServices} />
        </>
      ) : null}
    </>
  );
};

export default StoreInformation;
